import { useState, useEffect } from 'react';
import { Form, Image, Typography, Alert, message } from 'antd';
import CustomInput from '../../components/CustomInput/Input';
import CustomButton from '../../components/CustomButton/Button';
import { useNavigate } from 'react-router-dom';
import Logo from "../../assets/images/auth-logo.png";
import { useDispatch, useSelector } from "react-redux";
import { passwordRules, confirmPasswordRules } from '../../helpers/helpers';
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import CryptoJS from 'crypto-js';
import axios from '../../config/httpConfig';
import { clearMustChangePassword } from '../../redux/slices/AuthSlice';
import './Login.scss';

const { Title, Text } = Typography;

const ForceChangePassword = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [form] = Form.useForm();
    const { user } = useSelector((state) => state.auth);

    useEffect(() => {
        if (!user || !user.mustChangePassword) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    const toggleVisibility = () => {
        setVisible(!visible);
    };

    const onFinish = async (values) => {
        setLoading(true);
        setError(null);
        try {
            const oldPassword = CryptoJS.SHA256(values.oldPassword).toString();
            const newPassword = CryptoJS.SHA256(values.enterPassword).toString();
            const confirmPassword = CryptoJS.SHA256(values.confirmPassword).toString();

            const payload = {
                oldPassword,
                newPassword,
                confirmPassword
            };

            const data = await axios.post('/api/business/changePassword', payload);

            // Note: axios interceptor returns response.data directly,
            // so `data` here is already the API response body.
            const isSuccess = data?.statusCode === 200 || data?.statusCode === 201 || data?.status === 'success';

            if (isSuccess) {
                message.success(data?.response || "Password changed successfully.");
                dispatch(clearMustChangePassword());
                navigate('/dashboard');
            } else {
                setError(data?.response || data?.message || "Failed to change password.");
            }
        } catch (err) {
            setError(
                typeof err === 'string'
                    ? err
                    : err?.response || err?.message || "An error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="forgot-password-container">
            <div className="forgot-logo-container">
                <Image preview={false} src={Logo} alt="Logo" />
            </div>
            <div className="forgot-password-card-container">
                <Title level={2}>Set New Password</Title>
                <div style={{ marginBottom: 20 }}>
                    <Text type="secondary">
                        For security reasons, you must change your system-generated password before accessing the dashboard.
                    </Text>
                </div>
                <Form
                    form={form}
                    layout="vertical"
                    className="forgot-form"
                    onFinish={onFinish}
                >
                    <CustomInput
                        label="Current Password"
                        name="oldPassword"
                        type={visible ? "text" : "password"}
                        isPassword={true}
                        iconRender={(visible) =>
                            visible ? (
                                <EyeInvisibleOutlined onClick={toggleVisibility} />
                            ) : (
                                <EyeOutlined onClick={toggleVisibility} />
                            )
                        }
                        placeholder="Enter Current Password"
                        rules={[{ required: true, message: 'Please input your current password!' }]}
                    />

                    <CustomInput
                        label="Enter New Password"
                        name="enterPassword"
                        type={visible ? "text" : "password"}
                        isPassword={true}
                        iconRender={(visible) =>
                            visible ? (
                                <EyeInvisibleOutlined onClick={toggleVisibility} />
                            ) : (
                                <EyeOutlined onClick={toggleVisibility} />
                            )
                        }
                        placeholder="Enter New Password"
                        rules={passwordRules}
                    />

                    <CustomInput
                        label="Confirm New Password"
                        name="confirmPassword"
                        type={visible ? "text" : "password"}
                        isPassword={true}
                        iconRender={(visible) =>
                            visible ? (
                                <EyeInvisibleOutlined onClick={toggleVisibility} />
                            ) : (
                                <EyeOutlined onClick={toggleVisibility} />
                            )
                        }
                        placeholder="Re-enter New Password"
                        rules={confirmPasswordRules}
                    />
                    
                    {error && <Alert style={{ marginTop: 20 }} message={error} type="error" showIcon />}
                    
                    <Form.Item>
                        <CustomButton
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                            style={{ marginTop: 20, padding: 24 }}
                        >
                            Change Password
                        </CustomButton>
                    </Form.Item>
                </Form>
            </div>
        </div>
    );
};

export default ForceChangePassword;
